import React, { useState, useMemo } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex, Divider,
    Avatar, Input, Modal, Breadcrumb, Segmented, message, Empty
} from "antd";
import {
    UserOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined,
    CloseCircleOutlined, ArrowLeftOutlined, CheckOutlined, CloseOutlined,
    BookOutlined, MessageOutlined, QuestionCircleOutlined, TeamOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { JoinRequestDto, AdventureDto } from "../../commons/data/types";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

export const OrganizerJoinRequestsPage: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn shouldBeOrganizer={true}>
                <OrganizerJoinRequestsContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

const OrganizerJoinRequestsContent: React.FC = () => {
    const { adventureId } = useParams<{ adventureId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Modal state for approving/rejecting
    const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);
    const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
    const [selectedRequest, setSelectedRequest] = useState<JoinRequestDto | null>(null);
    const [responseNote, setResponseNote] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);

    // Fetch adventure details
    const { data: adventure } = useQuery<AdventureDto>(
        ['organizer-adventure', adventureId],
        () => axios.get(`/api/organizer/adventures`).then(res => (res.data || []).find((a: AdventureDto) => a.id === adventureId)),
        { enabled: !!adventureId }
    );

    // Fetch join requests for adventure
    const { data: joinRequests = [], isLoading, refetch } = useQuery<JoinRequestDto[]>(
        ['organizer-adventure-join-requests', adventureId],
        () => axios.get(`/api/organizer/adventures/${adventureId}/join-requests`).then(res => res.data),
        { enabled: !!adventureId }
    );

    const filteredAndSortedRequests = useMemo(() => {
        let result = [...joinRequests];
        if (statusFilter !== 'ALL') {
            result = result.filter(r => r.status === statusFilter);
        }
        result.sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.createdAt - b.createdAt;
            } else {
                return b.createdAt - a.createdAt;
            }
        });
        return result;
    }, [joinRequests, statusFilter, sortOrder]);

    const counts = useMemo(() => {
        const pending = joinRequests.filter(r => r.status === 'PENDING').length;
        const approved = joinRequests.filter(r => r.status === 'APPROVED').length;
        const rejected = joinRequests.filter(r => r.status === 'REJECTED').length;
        return { total: joinRequests.length, pending, approved, rejected };
    }, [joinRequests]);

    const openActionModal = (request: JoinRequestDto, type: 'approve' | 'reject') => {
        setSelectedRequest(request);
        setActionType(type);
        setResponseNote('');
        setActionModalOpen(true);
    };

    const handleConfirmAction = async () => {
        if (!selectedRequest) return;
        setProcessing(true);
        try {
            const url = `/api/organizer/join-requests/${selectedRequest.id}/${actionType}`;
            const payload = { note: responseNote.trim() || undefined };
            await axios.post(url, payload);

            message.success(
                actionType === 'approve'
                    ? t('organizer.joinRequests.success.approved', 'Join request approved successfully!')
                    : t('organizer.joinRequests.success.rejected', 'Join request declined.')
            );
            setActionModalOpen(false);
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || t('organizer.joinRequests.error.actionFailed', 'Failed to process request.'));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div style={{ maxWidth: '100%', margin: '30px auto', padding: '0 20px' }}>
            {/* BREADCRUMB */}
            <div style={{ marginBottom: 16 }}>
                <Flex justify="space-between" align="center">
                    <Breadcrumb
                        items={[
                            { title: <Link to="/organizer">{t('organizer.home.title', 'Organizer')}</Link> },
                            { title: <Link to="/organizer/adventures">{t('organizer.adventures.title', 'Adventures')}</Link> },
                            { title: adventure?.title || t('organizer.joinRequests.title', 'Join Requests') }
                        ]}
                    />
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/organizer/adventures')}>
                        {t('organizer.joinRequests.backToAdventures', 'Back to Adventures')}
                    </Button>
                </Flex>
            </div>

            {/* HEADER CARD */}
            <Card style={{ ...launcherCard, marginBottom: 24 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
                    <div>
                        <Title level={3} style={{ margin: '0 0 8px 0' }}>
                            <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            {t('organizer.joinRequests.heading', 'Join Requests for')} {adventure?.title}
                        </Title>
                        {adventure?.systemTitle && (
                            <Text type="secondary">
                                <BookOutlined /> {adventure.systemTitle}
                            </Text>
                        )}
                    </div>
                    <Space size="middle" wrap >
                        <Tag color="gold" style={{ fontSize: 13, padding: '4px 10px' }}>
                            {t('joinRequests.status.pending', 'Pending')}: {counts.pending}
                        </Tag>
                        <Tag color="green" style={{ fontSize: 13, padding: '4px 10px' }}>
                            {t('joinRequests.status.approved', 'Approved')}: {counts.approved}
                        </Tag>
                        <Tag color="red" style={{ fontSize: 13, padding: '4px 10px' }}>
                            {t('joinRequests.status.rejected', 'Declined')}: {counts.rejected}
                        </Tag>
                    </Space>
                </Flex>
            </Card>

            {/* CONTROLS (FILTER & SORT) */}
            <Card style={{ ...launcherCard, marginBottom: 24, padding: '12px 20px' }}>
                <Flex justify="space-between" align="start" gap={"small"} vertical={false}>
                    <Segmented
                        vertical
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val as string)}
                        options={[
                            { label: `${t('common.all', 'All')} (${counts.total})`, value: 'ALL' },
                            { label: `${t('joinRequests.status.pending', 'Pending')} (${counts.pending})`, value: 'PENDING' },
                            { label: `${t('joinRequests.status.approved', 'Approved')} (${counts.approved})`, value: 'APPROVED' },
                            { label: `${t('joinRequests.status.rejected', 'Declined')} (${counts.rejected})`, value: 'REJECTED' }
                        ]}
                    />

                    <Segmented
                        vertical
                        value={sortOrder}
                        onChange={(val) => setSortOrder(val as 'asc' | 'desc')}
                        options={[
                            { label: t('organizer.joinRequests.sort.newestFirst', 'Newest First'), value: 'desc' },
                            { label: t('organizer.joinRequests.sort.oldestFirst', 'Oldest First'), value: 'asc' }
                        ]}
                    />
                </Flex>
            </Card>

            {/* REQUESTS LIST */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large" />
                </div>
            ) : filteredAndSortedRequests.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {filteredAndSortedRequests.map(req => (
                        <JoinRequestItemCard
                            key={req.id}
                            request={req}
                            onApprove={() => openActionModal(req, 'approve')}
                            onReject={() => openActionModal(req, 'reject')}
                        />
                    ))}
                </div>
            ) : (
                <Card style={{ ...launcherCard, textAlign: 'center', padding: '40px 0' }}>
                    <Empty description={t('organizer.joinRequests.none', 'No join requests match your filter.')} />
                </Card>
            )}

            {/* APPROVE / REJECT MODAL */}
            <Modal
                title={
                    actionType === 'approve'
                        ? t('organizer.joinRequests.modal.approveTitle', 'Approve Join Request')
                        : t('organizer.joinRequests.modal.rejectTitle', 'Decline Join Request')
                }
                open={actionModalOpen}
                onCancel={() => setActionModalOpen(false)}
                onOk={handleConfirmAction}
                confirmLoading={processing}
                okText={actionType === 'approve' ? t('organizer.joinRequests.modal.confirmApprove', 'Approve Request') : t('organizer.joinRequests.modal.confirmReject', 'Decline Request')}
                okButtonProps={{
                    danger: actionType === 'reject',
                    style: actionType === 'approve' ? { background: '#52c41a', borderColor: '#52c41a' } : undefined
                }}
            >
                <div style={{ marginTop: 16 }}>
                    <Paragraph>
                        {actionType === 'approve'
                            ? t('organizer.joinRequests.modal.approveDesc', 'Approving this request will reserve seats for the player and confirm their participation.')
                            : t('organizer.joinRequests.modal.rejectDesc', 'Are you sure you want to decline this player\'s join request?')}
                    </Paragraph>

                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        {t('organizer.joinRequests.modal.noteLabel', 'Optional Response Note to Player (max 150 chars)')}
                    </Text>
                    <TextArea
                        style={{ marginBottom: '1em' }}
                        value={responseNote}
                        onChange={(e) => setResponseNote(e.target.value)}
                        maxLength={150}
                        showCount
                        rows={3}
                        placeholder={
                            actionType === 'approve'
                                ? t('organizer.joinRequests.modal.approveNotePlaceholder', 'e.g. Welcome to the group! We will play using Discord...')
                                : t('organizer.joinRequests.modal.rejectNotePlaceholder', 'e.g. Sorry, the party balance is currently full...')
                        }
                    />
                </div>
            </Modal>
        </div>
    );
};

interface JoinRequestItemCardProps {
    request: JoinRequestDto;
    onApprove: () => void;
    onReject: () => void;
}

const JoinRequestItemCard: React.FC<JoinRequestItemCardProps> = ({ request, onApprove, onReject }) => {
    const { t } = useTranslation();
    const isPending = request.status === 'PENDING';
    const isApproved = request.status === 'APPROVED';
    const isRejected = request.status === 'REJECTED';

    return (
        <Card
            style={{
                ...launcherCard,
                borderRadius: 10,
                border: isPending ? '1px solid #ffe58f' : isApproved ? '1px solid #b7eb8f' : isRejected ? '1px solid #ffccc7' : '1px solid #f0f0f0',
                background: '#fff',
                padding: '20px'
            }}
        >
            <Flex justify="space-between" align="start" wrap="wrap" gap="middle">
                {/* PLAYER INFO */}
                <Flex gap="middle" align="center">
                    <Avatar
                        src={request.playerPicture}
                        icon={<UserOutlined />}
                        size={48}
                    />
                    <div>
                        <Flex align="center" gap="small">
                            <Title level={5} style={{ margin: 0 }}>
                                {request.playerName || 'Anonymous Player'}
                            </Title>
                            {request.playerNickname && (
                                <Text type="secondary">@{request.playerNickname}</Text>
                            )}
                        </Flex>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <ClockCircleOutlined /> {dayjs(request.createdAt).format('YYYY-MM-DD HH:mm')}
                        </Text>
                    </div>
                </Flex>

                {/* STATUS & ACTIONS */}
                <Flex align="center" gap="small">
                    {isApproved && (
                        <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>
                            {t('joinRequests.status.approved', 'Approved')}
                        </Tag>
                    )}
                    {isPending && (
                        <>
                            <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>
                                {t('joinRequests.status.pending', 'Pending Review')}
                            </Tag>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                size="small"
                                onClick={onApprove}
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            >
                                {t('common.approve', 'Approve')}
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                size="small"
                                onClick={onReject}
                            >
                                {t('common.reject', 'Decline')}
                            </Button>
                        </>
                    )}
                    {isRejected && (
                        <Tag color="red" icon={<CloseCircleOutlined />} style={{ fontSize: 13, padding: '3px 10px' }}>
                            {t('joinRequests.status.rejected', 'Declined')}
                        </Tag>
                    )}
                </Flex>
            </Flex>

            {/* PLAYER NOTE */}
            {request.playerNote && (
                <div style={{ marginTop: 14, background: '#fafafa', padding: '10px 14px', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                    <Text strong style={{ fontSize: 12, color: '#666' }}>
                        <MessageOutlined /> {t('joinRequests.detail.playerNote', 'Player Note')}:
                    </Text>
                    <Paragraph style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                        {request.playerNote}
                    </Paragraph>
                </div>
            )}

            {/* ORGANIZER RESPONSE NOTE */}
            {request.organizerResponseNote && (
                <div style={{ marginTop: 10, background: isApproved ? '#f6ffed' : '#fff2f0', padding: '10px 14px', borderRadius: 6, border: isApproved ? '1px solid #b7eb8f' : '1px solid #ffccc7' }}>
                    <Text strong style={{ fontSize: 12, color: isApproved ? '#389e0d' : '#cf1322' }}>
                        {t('joinRequests.detail.yourResponse', 'Your Response')}:
                    </Text>
                    <Paragraph style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                        {request.organizerResponseNote}
                    </Paragraph>
                </div>
            )}

            {/* SESSIONS RSVP BREAKDOWN */}
            <Divider style={{ margin: '14px 0' }} />
            <div>
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> {t('joinRequests.detail.sessionAvailability', 'Session Availability')}:
                </Text>
                <Row gutter={[12, 12]}>
                    {(request.sessionRsvps || []).map((s, idx) => (
                        <Col xs={24} sm={12} key={s.sessionId || idx}>
                            <Flex justify="space-between" align="center" style={{ background: '#fafafa', padding: '8px 12px', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                <Text style={{ fontSize: 12 }}>
                                    #{idx + 1}: {s.sessionTime ? dayjs(s.sessionTime).format('MMM D, HH:mm') : 'Session'}
                                </Text>
                                {s.rsvp === 'YES' && (
                                    <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                        YES
                                    </Tag>
                                )}
                                {s.rsvp === 'MAYBE' && (
                                    <Tag color="gold" icon={<QuestionCircleOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                        MAYBE
                                    </Tag>
                                )}
                                {s.rsvp === 'NO' && (
                                    <Tag color="default" icon={<CloseCircleOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                        NO
                                    </Tag>
                                )}
                            </Flex>
                        </Col>
                    ))}
                </Row>
            </div>
        </Card>
    );
};
